import type { CreateItemType } from "ls/features/workspaces/useCreateItems";
import type { ShortcutJs } from "@/generated/models";
import * as v from "valibot";
import { ShortcutState } from "@/common/fs/types";
import { EmptyGuid } from "@/common/lib";
import { ShortcutType } from "@/generated/models";

const DiskoTypesSchema = v.union([
  v.literal(ShortcutType.Whiteboard),
  v.literal(ShortcutType.Document),
  v.literal(ShortcutType.Design),
]);

const TemplateProjectTypesSchema = v.union([
  v.literal(ShortcutType.Whiteboard),
  v.literal(ShortcutType.Document),
]);

const LibraryProjectTypesSchema = v.union([
  v.literal(ShortcutType.Whiteboard),
]);

const RpProjectTypesSchema = v.union([
  v.literal(ShortcutType.Rp),
  v.literal(ShortcutType.Lib),
  v.literal(ShortcutType.TeamLib),
  v.literal(ShortcutType.TeamRp),
]);

const ClickthroughProjectTypesSchema = v.union([
  v.literal(ShortcutType.Expo),
]);

const TeamProjectTypesSchema = v.union([
  v.literal(ShortcutType.TeamLib),
  v.literal(ShortcutType.TeamRp),
]);

export type DiskoType = v.InferOutput<typeof DiskoTypesSchema>;
export type RpProjectType = v.InferOutput<typeof RpProjectTypesSchema>;
export type ClickthroughProjectType = v.InferOutput<typeof ClickthroughProjectTypesSchema>;
export type TeamProjectType = v.InferOutput<typeof TeamProjectTypesSchema>;

const MinRP7Build = 3100;
const MinRP8Build = 3200;
const MinRP9Build = 3600;
const MinRP10Build = 3800;
const MinRP11Build = 4100;

const MinRP9BuildWithThumbnails = 3629;
const MinRP9HgRevWithThumbnails = 17532;
const MinRP10HgRev = 21000;

export interface FileOverridesOptions {
  resourceDirVersion?: number;
  shortcutState?: string;
}
export function isRp6ProjectAndBelow(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion = file.ResourceDirVersion, shortcutState = file.ShortcutState } = options;
  if (shortcutState
    && (shortcutState === ShortcutState.Generating
      || shortcutState === ShortcutState.Empty
      || shortcutState === ShortcutState.GenerationFailed)) {
    return false;
  }
  if (!resourceDirVersion) return false;
  return resourceDirVersion < MinRP7Build;
}

export function isRp7Project(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return revNumber < 10000 && revNumber >= MinRP7Build && revNumber < MinRP8Build;
}

export function isRp8Project(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return revNumber < 10000 && revNumber >= MinRP8Build && revNumber < MinRP9Build;
}

export function isRp9Project(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return revNumber < 10000 && revNumber >= MinRP9Build && revNumber < MinRP10Build;
}

export function isRp9ProjectAndAbove(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return revNumber >= MinRP9Build;
}

export function isRp9ProjectWithThumbnails(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return ((revNumber >= MinRP9HgRevWithThumbnails && revNumber < MinRP10HgRev)
    || (revNumber < 10000 && revNumber >= MinRP9BuildWithThumbnails && revNumber < MinRP10Build));
}

export function isRp10Project(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return (revNumber >= MinRP10HgRev || (revNumber < 10000 && revNumber >= MinRP10Build));
}

export function isRp11Project(file: ShortcutJs, options: FileOverridesOptions = {}) {
  const { resourceDirVersion: revNumber = file.ResourceDirVersion } = options;
  if (!revNumber) return false;
  return (revNumber < 10000 && revNumber >= MinRP11Build);
}

export function getRpVersion(file: ShortcutJs) {
  if (!isRpProject(file)) return undefined;
  if (isRp11Project(file)) return 11;
  if (isRp10Project(file)) return 10;
  if (isRp9Project(file)) return 9;
  if (isRp8Project(file)) return 8;
  if (isRp7Project(file)) return 7;
  if (isRp6ProjectAndBelow(file)) return 6;
  return undefined;
}

export function isThumbnailSupportedProject(file: ShortcutJs) {
  return (!!file.ShortcutState && file.ShortcutState !== ShortcutState.GenerationFailed)
    && (isClickthroughProjectType(file.ShortcutType)
      || isRp9ProjectWithThumbnails(file)
      || isRp10Project(file)
      || file.ShortcutState === ShortcutState.Empty);
}

export function projectWasNotGenerated(file: ShortcutJs) {
  return file.ShortcutState === ShortcutState.Generating || file.ShortcutState === ShortcutState.GenerationFailed;
}

export function isPublicInspectSupportedProject(file: ShortcutJs) {
  return isClickthroughProject(file) || (isRpProject(file) && isRp10Project(file));
}

export function isClickthroughProject(file: ShortcutJs) {
  return isClickthroughProjectType(file.ShortcutType);
}

export function isDiskoProject(file: ShortcutJs) {
  return isDiskoProjectType(file.ShortcutType);
}

export function isBookmarkShortcut(file: ShortcutJs) {
  return isBookmarkShortcutType(file.ShortcutType);
}

export function isDocumentProject(file: ShortcutJs) {
  return isDocumentProjectType(file.ShortcutType);
}

export function isRpProject(file: ShortcutJs) {
  return isRpProjectType(file.ShortcutType);
}

export function isTeamProject(file: ShortcutJs) {
  return isTeamProjectType(file.ShortcutType);
}

export function isLibProject(file: ShortcutJs) {
  return isLibProjectType(file.ShortcutType);
}

export function isTeamLibProject(file: ShortcutJs) {
  return isTeamLibProjectType(file.ShortcutType);
}

export function isDesignProject(file: ShortcutJs) {
  return isDesignProjectType(file.ShortcutType);
}

export function isWhiteboardProject(file: ShortcutJs) {
  return isWhiteboardProjectType(file.ShortcutType);
}

export function isClickthroughProjectType(type: ShortcutType) {
  return v.is(ClickthroughProjectTypesSchema, type);
}

export function isDocumentProjectType(type: ShortcutType) {
  return type === ShortcutType.Document;
}

export function isDocumentIncludedProjectType(type: ShortcutType) {
  return isWhiteboardProjectType(type) || isDocumentProjectType(type);
}

export function isDiskoProjectType(type: number) {
  return v.is(DiskoTypesSchema, type);
}

export function isRpProjectType(type: ShortcutType) {
  return v.is(RpProjectTypesSchema, type);
}

export function isTeamProjectType(type: ShortcutType) {
  return v.is(TeamProjectTypesSchema, type);
}

export function isLibProjectType(type: ShortcutType) {
  return type === ShortcutType.Lib;
}

export function isTeamLibProjectType(type: ShortcutType) {
  return type === ShortcutType.TeamLib;
}

export function isDesignProjectType(type: ShortcutType) {
  return type === ShortcutType.Design;
}

export function isWhiteboardProjectType(type: ShortcutType) {
  return type === ShortcutType.Whiteboard;
}

export function isBookmarkShortcutType(type: ShortcutType) {
  return type === ShortcutType.Bookmark;
}

export function canBeTemplate(file: ShortcutJs) {
  return isCanBeTemplateType(file.ShortcutType);
}

export function isCanBeTemplateType(type: ShortcutType) {
  return v.is(TemplateProjectTypesSchema, type);
}

export function canBeLibrary(file: ShortcutJs) {
  return isCanBeLibraryType(file.ShortcutType);
}

export function isCanBeLibraryType(type: ShortcutType) {
  return v.is(LibraryProjectTypesSchema, type);
}

const RP_WITH_QUERY_SUPPORT = 3891;
export function isQuerySupportProject(file: ShortcutJs) {
  if (isClickthroughProjectType(file.ShortcutType)) return true;
  const revNumber = file.ResourceDirVersion;
  if (!revNumber) return false;
  return revNumber < 10000 && revNumber >= RP_WITH_QUERY_SUPPORT;
}

export function isEmbeddingAllowed(type: ShortcutType): boolean {
  return isRpProjectType(type)
    || isClickthroughProjectType(type)
    || isDiskoProjectType(type);
}

const LikeNumberSchema = v.union([
  v.number(),
  v.pipe(v.string(), v.transform(Number), v.number()),
]);

const ShortcutTypeSchema = v.pipe(LikeNumberSchema, v.enum(ShortcutType));

export function toProjectType(type: string) {
  const result = v.safeParse(ShortcutTypeSchema, type);
  return result.success ? result.output : undefined;
}

export function getFileTypeEnumString(type: ShortcutJs) {
  return fileTypeToEnumString(type.ShortcutType);
}

export function fileTypeToEnumString(type: ShortcutType) {
  if (type === ShortcutType.Rp) return "Rp";
  if (type === ShortcutType.TeamRp) return "TeamRp";
  if (type === ShortcutType.Lib) return "Lib";
  if (type === ShortcutType.TeamLib) return "TeamLib";
  if (type === ShortcutType.Expo) return "Expo";
  if (type === ShortcutType.Whiteboard) return "Whiteboard";
  if (type === ShortcutType.Document) return "Document";
  if (type === ShortcutType.Design) return "Design";
  if (type === ShortcutType.Bookmark) return "Bookmark";
  return "";
}

export function fileTypeToCreateItemType(type: ShortcutType): CreateItemType | undefined {
  if (type === ShortcutType.Rp) return "prototype";
  if (type === ShortcutType.TeamRp) return "prototype";
  if (type === ShortcutType.Lib) return "prototype";
  if (type === ShortcutType.TeamLib) return "prototype";
  if (type === ShortcutType.Expo) return "expo";
  if (type === ShortcutType.Whiteboard) return "whiteboard";
  if (type === ShortcutType.Document) return "document";
  if (type === ShortcutType.Design) return "design";
  if (type === ShortcutType.Bookmark) return "bookmark";
  return undefined;
}

export function isFileInPrivateWorkspace(file: ShortcutJs) {
  return file.SubInstanceId === EmptyGuid;
}
